import { useMemo, useState } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  isVendorTaxonomyKey as isSearchableVendorTaxonomyKey,
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import FONT from '@zola/zola-ui/src/styles/emotion/fonts';
import MEDIA_QUERY from '@zola/zola-ui/src/styles/emotion/mediaQuery';

import styled from '@emotion/styled';

import { EditCategoryModalProps } from '~/components/common/modals/EditCategoryModal/EditCategoryModal';
import { useModal } from '~/contexts/ModalContext';
import { useSRPSearchBarContext } from '~/pages/couples/explore/components/SearchResults/components/SearchControls/SRPSearchBarContext';
import { SharedPageTitle } from '~/pages/couples/explore/components/SearchResults/components/SharedPageTitle/SharedPageTitle';
import { useVendorSearchNavigation } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationContext';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { isSearchLocation } from '~/util/searchUtils';
import TextUtils from '~/util/textUtils';

import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';
import { TotalVendorsDescriptionProps } from '../TotalVendorsDescription/types';

const FormattedCount = styled.span`
  display: inline;
  margin: 0;
  padding: 0;
  color: ${COLORS3.BLACK_100};
  font-size: ${FONT.SIZE.REGULAR};
  font-weight: ${FONT.WEIGHT.SEMI_BOLD} !important;
  line-height: 28px;
  letter-spacing: -0.03em;
  ${MEDIA_QUERY.EXCLUDE_MOBILE} {
    font-size: ${FONT.SIZE.LARGE};
    line-height: 32px;
  }
`;

const FirstWord = styled.span`
  display: inline-block;
  &::first-letter {
    text-transform: lowercase;
  }
  ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
    text-decoration: underline;
  }
`;

const getCategoryHeading = (selectedTaxonomy: VendorTaxonomyKey | undefined) => {
  if (isSearchableVendorTaxonomyKey(selectedTaxonomy)) {
    return categoryHeadings(selectedTaxonomy);
  }
  return 'vendors';
};

const categoryHeadings = getVendorTaxonomySwitchFunc<SearchableVendorTaxonomyKey, string>({
  [BANDS_DJS_TAXONOMY_KEY]: 'bands and DJs',
  [BAR_SERVICES_TAXONOMY_KEY]: 'bar services & beverages',
  [CAKES_DESSERTS_TAXONOMY_KEY]: 'cakes & desserts',
  [CATERING_TAXONOMY_KEY]: 'caterers',
  [FLORISTS_TAXONOMY_KEY]: 'florists',
  [HAIR_MAKEUP_TAXONOMY_KEY]: 'beauty professionals',
  [PHOTOGRAPHERS_TAXONOMY_KEY]: 'photographers',
  [PLANNERS_TAXONOMY_KEY]: 'planners',
  [VENUES_TAXONOMY_KEY]: 'venues',
  [VIDEOGRAPHERS_TAXONOMY_KEY]: 'videographers',
});

export const PageTitle = ({
  totalHits,
  selectedTaxonomy,
  selectedLocation,
  selectedFacets,
}: TotalVendorsDescriptionProps) => {
  const { showV2PredefinedModal } = useModal();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const { setNextTaxonomySearch, updateSelectedLocation } = useSRPSearchBarContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  const maxShown = selectedTaxonomy || selectedLocation ? 1000 : 10000;
  const numShown = Math.min(maxShown, totalHits);
  let formattedCount = TextUtils.formatWithCommas(numShown);

  if (numShown === maxShown) {
    formattedCount = `${formattedCount}+`;
  }

  const description = useMemo(() => {
    const facetDescription = getCanonicalFacetDescription(selectedTaxonomy, selectedFacets) || '';
    const weddingPrefix = facetDescription ? 'wedding' : 'Wedding';
    return `${facetDescription} ${weddingPrefix}`.trim();
  }, [selectedFacets, selectedTaxonomy]);

  const category = useMemo(() => getCategoryHeading(selectedTaxonomy), [selectedTaxonomy]);

  const location = useMemo(
    () => (isSearchLocation(selectedLocation) ? selectedLocation.label : undefined),
    [selectedLocation]
  );

  const onCategoryClick = () => {
    showV2PredefinedModal<EditCategoryModalProps>(
      'EDIT_CATEGORY_MODAL',
      {
        dialogHeadingId: 'edit-category-heading',
        triggerRef: null,
        lockBgScrolling: true,
      },
      {
        onSelect: (newTaxonomy) => {
          setNextTaxonomySearch(newTaxonomy);
          navigateToSrp({ selectedTaxonomy: newTaxonomy });
        },
      }
    );
  };

  const onLocationClick = () => {
    setShowLocationModal(true);
  };

  const onUpdateSelectedLocation = (newLocation: LocationOrAllMarketsType) => {
    updateSelectedLocation(newLocation);
    navigateToSrp({ selectedLocation: newLocation });
  };

  return (
    <SharedPageTitle
      category={category}
      description={<FirstWord>{description}</FirstWord>}
      formattedCount={<FormattedCount>{formattedCount}&nbsp;</FormattedCount>}
      location={location}
      onCategoryClick={onCategoryClick}
      onLocationClick={onLocationClick}
      onUpdateSelectedLocation={onUpdateSelectedLocation}
      setShowLocationModal={setShowLocationModal}
      showLocationModal={showLocationModal}
    />
  );
};
